import { Collapse, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import type { InitiativeListItem } from "../../api/initiatives";

const LINE_CLAMP = 2;
const FALLBACK_TEXT = "No description.";

export interface SummaryReadMorePopoverProps {
  initiative: InitiativeListItem;
  lineClamp?: number;
  placement?: "left" | "right";
}

export function SummaryReadMorePopover({
  initiative,
  lineClamp = LINE_CLAMP,
}: SummaryReadMorePopoverProps) {
  const [expanded, setExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const clampRef = useRef<HTMLDivElement | null>(null);
  const text = initiative.summary?.trim() || FALLBACK_TEXT;

  useEffect(() => {
    const el = clampRef.current;
    if (!el) return;
    const hasOverflow = el.scrollHeight > el.clientHeight;
    setShowReadMore(hasOverflow);
  }, [text]);

  const handleToggle = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  return (
    <Stack flex={1} minHeight={0} spacing={0.25}>
      {!expanded && (
        <Typography
          ref={clampRef}
          variant="small-ibmPlexMono"
          color="text.primary"
          sx={{
            display: "-webkit-box",
            overflow: "hidden",
            WebkitLineClamp: lineClamp,
            WebkitBoxOrient: "vertical",
          }}
        >
          {text}
        </Typography>
      )}
      <Collapse in={expanded}>
        <Typography
          variant="small-ibmPlexMono"
          color="text.primary"
          sx={{ whiteSpace: "pre-wrap" }}
        >
          {text}
        </Typography>
      </Collapse>
      {showReadMore && (
        <Typography
          component="button"
          type="button"
          variant="small-ibmPlexMono"
          color="primary"
          fontWeight={500}
          align="left"
          onClick={handleToggle}
          aria-expanded={expanded}
          aria-label={expanded ? "Read less" : "Read more"}
          border={0}
          p={0}
          alignSelf="flex-start"
          sx={{
            cursor: "pointer",
            background: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {expanded ? "Read less" : "Read more"}
        </Typography>
      )}
    </Stack>
  );
}
